// LoginPage — chỉ wire @cnv/ui/pages/auth/LoginPage với branding + Google button SaaS-specific.
//
// Page logic + form sống ở @cnv/ui per HOST_CONTRACT.md (1 cty = 1 component lib).
// Host's `@host/auth.useAuth()` (shim AuthContext.jsx) cung cấp:
//   - login(email, pwd) → authApi.login + setSession
//   - loginWithGoogle(idToken) → authApi.loginWithGoogle + setSession
//   - listMyTenants() — multi-tenant routing /select-workspace vs /chat
//
// Google sign-in của SaaS là nút GSI auth-code (components/GoogleCodeButton, dùng chung với trang nhận
// lời mời) → host responsibility, mount qua googleSlot.

import { useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import LoginPage from '@cnv/ui/pages/auth/LoginPage';
import { isLoginChallengeError } from '@cnv/ui/auth/LoginChallenge';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import GoogleCodeButton, { GOOGLE_CLIENT_ID } from '../components/GoogleCodeButton';
import { peekOAuthReturn } from '../lib/oauthReturn';
import '../i18n-host';

// msg`` = descriptor (chưa dịch) — hằng module scope không gọi hook được. LoginPage của
// cnv-ui nhận cả descriptor lẫn chuỗi thường trong errorMap (xem translate() bên đó).
const SAAS_ERROR_MAP = {
  account_not_registered: msg`Email Google chưa đăng ký — vui lòng đăng ký tài khoản trước`,
  // Bị khoá ≠ chưa đăng ký. Trước 2026-09-07 BE gộp 2 ca này thành `account_not_registered` nên
  // người đang bị khoá được bảo đi "đăng ký tài khoản trước" (dunglt@cnv.vn).
  user_disabled: msg`Tài khoản của bạn đã bị khoá. Liên hệ quản trị viên để mở khoá.`,
  workspace_locked: msg`Tài khoản của bạn đang bị khoá ở workspace này. Liên hệ quản trị viên workspace.`,
  google_id_token_invalid: msg`Google token không hợp lệ, thử lại`,
  google_no_email: msg`Tài khoản Google không có email`,
  google_oauth_not_configured: msg`Server chưa cài đặt Google OAuth, báo admin`,
  no_accessible_tenant: msg`Không có workspace khả dụng — liên hệ admin`,
};

// POST /auth/google với code của nút — login + lưu refresh token calendar vào
// metadata.google_calendar (server-side, bền vững).
function GoogleButton() {
  const { loginWithGoogle } = useAuth();
  const { t, i18n } = useLingui();

  const handleCode = async (code) => {
    try {
      const user = await loginWithGoogle({ code });
      const who = user?.display_name || user?.email || '';
      toast.success(t`Xin chào ${who}`);
      window.dispatchEvent(new Event('cnv:auth-changed'));
    } catch (err) {
      // Cần thêm bước (xác thực 2 lớp…) ⇒ AuthContext đã giữ bước đó, LoginPage của cnv-ui đổi sang màn nhập mã.
      if (isLoginChallengeError(err)) return;
      const errCode = err?.response?.data?.error?.code;
      toast.error(SAAS_ERROR_MAP[errCode] ? i18n._(SAAS_ERROR_MAP[errCode]) : t`Đăng nhập Google thất bại — thử lại`);
    }
  };

  return <GoogleCodeButton onCode={handleCode} />;
}

export default function LoginPageSaas() {
  const { t } = useLingui();
  // SaaS routing sau đăng nhập:
  //   - Nhiều workspace → /select-workspace để người dùng chọn.
  //   - 1 workspace: chỉ vào /chat khi tenant đã bật module team_chat; ngược lại
  //     /dashboard (an toàn cho mọi tenant). Trước đây ép /chat → tenant tắt
  //     team_chat gặp lỗi "Module team_chat chưa enable" (TC-292 / ticket 46d0d32b).
  const redirectTo = (_user, tenants) => {
    // Đang giữa luồng «Kết nối CNV Work» của ứng dụng AI (OAuth MCP) ⇒ quay lại màn đồng ý, kể cả khi có nhiều
    // workspace — màn đó tự cho chọn workspace. Xem lib/oauthReturn.js.
    const oauthBack = peekOAuthReturn();
    if (oauthBack) return oauthBack;
    if (Array.isArray(tenants) && tenants.length > 1) return '/select-workspace';
    const onlyTenant = Array.isArray(tenants) && tenants.length === 1 ? tenants[0] : null;
    const teamChatEnabled = !!onlyTenant?.private_modules?.team_chat;
    return teamChatEnabled ? '/chat' : '/dashboard';
  };

  return (
    <LoginPage
      logoSrc="/cnvwork.png"
      logoAlt="CNV Work"
      brandName="CNV Work"
      tagline="CRM · OPERATIONS · AI AGENT"
      // subtitle/signupText/signupCtaText: KHÔNG truyền — dùng default đã dịch của
      // @cnv/ui LoginPage ("Chào mừng trở lại" / "Chưa có tài khoản?" / "Đăng ký miễn phí").
      subtitleHint={t`Đăng nhập để tiếp tục quản lý công việc`}
      variant="pink"
      googleSlot={GOOGLE_CLIENT_ID ? <GoogleButton /> : null}
      signupHref="/signup"
      // Chính sách 15/09/2026: workspace mới dùng thử 14 ngày (không còn Free trọn đời); không giới hạn tài khoản từ 19/10/2026.
      belowCard={t`Dùng thử miễn phí 14 ngày — đủ tính năng CNV Work, không giới hạn tài khoản`}
      errorMap={SAAS_ERROR_MAP}
      redirectTo={redirectTo}
    />
  );
}
